// ── Contract Offer — PDF engine ───────────────────────────────────────────────
// Renders the Driver Review "Offer Workbench" offer as a real, filed-and-signable
// PDF (pdf-lib — the same lib the lease/agreement flows use), so it can be pushed to
// the driver's portal My Docs, retained in the engine, and read-receipted.
//
// It takes the SAME structured data the on-screen print builds from (see
// buildOfferData() in DriverReviewPage), so the PDF mirrors the workbench exactly.
//
//   buildOfferPdfBytes(data) → Uint8Array (a real PDF).
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

const NAVY  = rgb(0.102, 0.102, 0.18);   // #1a1a2e
const GOLD  = rgb(0.788, 0.659, 0.298);  // #c9a84c
const INK   = rgb(0.12, 0.14, 0.19);
const GREY  = rgb(0.42, 0.45, 0.5);
const GREEN = rgb(0.18, 0.49, 0.196);    // #2e7d32
const LINE  = rgb(0.905, 0.913, 0.945);
const CREAM = rgb(0.968, 0.953, 0.909);  // #f7f3e8
const CREAM_BD = rgb(0.894, 0.851, 0.722);

// StandardFonts (WinAnsi) can't encode some typographic glyphs — map them to safe ones.
const san = (s) => String(s)
  .replace(/−/g, '-').replace(/≈/g, '~').replace(/→/g, '->')
  .replace(/…/g, '...').replace(/[‘’]/g, "'").replace(/[“”]/g, '"')
  .replace(/[^\x00-\xFF–—€•]/g, '');   // strip anything else outside WinAnsi

const money = (n, d = 2) => `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const money0 = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;
const int0 = (n) => Math.round(Number(n) || 0).toLocaleString('en-AU');
// Decimal hours → "1h 20m" / "45m" — how a driver reads a shift, not "1.33".
const hhmm = (h) => {
  const mins = Math.round(Math.max(0, Number(h) || 0) * 60);
  const hh = Math.floor(mins / 60), mm = mins % 60;
  return hh && mm ? `${hh}h ${mm}m` : hh ? `${hh}h` : `${mm}m`;
};

export async function buildOfferPdfBytes(data) {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const PW = 595.28, PH = 841.89;          // A4 portrait (pt)
  const M = 46, RIGHT = PW - M, COLW = RIGHT - M;
  let page, y;

  const newPage = () => { page = pdf.addPage([PW, PH]); y = PH - 40; drawBand(); y = PH - 96; };
  const ensure = (need) => { if (y - need < 60) newPage(); };

  const drawBand = () => {
    page.drawRectangle({ x: 0, y: PH - 66, width: PW, height: 66, color: NAVY });
    page.drawText('HOLLYPAUL', { x: M, y: PH - 40, size: 19, font: bold, color: GOLD });
    page.drawText('Delivery Contractor Offer', { x: M, y: PH - 54, size: 8, font, color: rgb(0.81, 0.83, 0.86) });
    const r1 = 'PH Bouveng / Hollypaul', r2 = 'hollypaul.com';
    page.drawText(r1, { x: RIGHT - font.widthOfTextAtSize(r1, 8), y: PH - 38, size: 8, font, color: rgb(0.81, 0.83, 0.86) });
    page.drawText(r2, { x: RIGHT - font.widthOfTextAtSize(r2, 8), y: PH - 50, size: 8, font, color: rgb(0.81, 0.83, 0.86) });
  };

  const text = (s, x, size, f = font, color = INK) => page.drawText(san(s), { x, y: y - size, size, font: f, color });
  const textR = (s, x, size, f = font, color = INK) => { const t = san(s); page.drawText(t, { x: x - f.widthOfTextAtSize(t, size), y: y - size, size, font: f, color }); };

  const wrap = (s, f, size, maxW) => {
    const words = san(s).split(/\s+/); const lines = []; let ln = '';
    for (const w of words) { const t = ln ? `${ln} ${w}` : w; if (f.widthOfTextAtSize(t, size) > maxW && ln) { lines.push(ln); ln = w; } else ln = t; }
    if (ln) lines.push(ln); return lines;
  };

  const h2 = (s) => {
    ensure(30); y -= 12;
    text(s.toUpperCase(), M, 11, bold, GOLD);
    page.drawLine({ start: { x: M, y: y - 16 }, end: { x: RIGHT, y: y - 16 }, thickness: 1.5, color: LINE });
    y -= 24;
  };

  // Draw a table. cols: [{label, w, align}]; rows: [[cell,...]] where cell = {t,bold,color} or string.
  const table = (cols, rows) => {
    ensure(24);
    // header
    page.drawRectangle({ x: M, y: y - 18, width: COLW, height: 18, color: NAVY });
    let cx = M;
    cols.forEach((c) => { const s = c.label; const tx = c.align === 'right' ? cx + c.w - 8 - bold.widthOfTextAtSize(s, 8) : cx + 8; page.drawText(s, { x: tx, y: y - 13, size: 8, font: bold, color: rgb(1, 1, 1) }); cx += c.w; });
    y -= 18;
    rows.forEach((r, ri) => {
      ensure(20);
      if (r._hl) page.drawRectangle({ x: M, y: y - 20, width: COLW, height: 20, color: CREAM });
      cx = M;
      cols.forEach((c, ci) => {
        const cell = r[ci] || {}; const s = typeof cell === 'string' ? cell : (cell.t ?? '');
        const f = (cell.bold || r._hl) ? bold : font; const col = cell.color || INK;
        const t = san(s);
        const tx = c.align === 'right' ? cx + c.w - 8 - f.widthOfTextAtSize(t, 9) : cx + 8;
        page.drawText(t, { x: tx, y: y - 14, size: 9, font: f, color: col });
        cx += c.w;
      });
      page.drawLine({ start: { x: M, y: y - 20 }, end: { x: RIGHT, y: y - 20 }, thickness: 0.5, color: LINE });
      y -= 20;
    });
    y -= 4;
  };

  const para = (s, size = 10, color = GREY) => {
    for (const ln of wrap(s, font, size, COLW)) { ensure(size * 1.5); text(ln, M, size, font, color); y -= size * 1.5; }
  };

  // ── Page 1 ──────────────────────────────────────────────────────────────────
  newPage();
  text(`Contract Offer — ${data.name}`, M, 17, bold, NAVY); y -= 22;
  text(data.sub, M, 10, font, GREY); y -= 22;

  // Hero: annual contract fee
  {
    ensure(74);
    const hH = 70;
    page.drawRectangle({ x: M, y: y - hH, width: COLW, height: hH, color: CREAM, borderColor: CREAM_BD, borderWidth: 1 });
    page.drawRectangle({ x: M, y: y - hH, width: 5, height: hH, color: GOLD });
    let hy = y - 16;
    page.drawText('YOUR ANNUAL CONTRACT FEE', { x: M + 18, y: hy, size: 9, font: bold, color: rgb(0.54, 0.48, 0.24) }); hy -= 22;
    page.drawText(money0(data.contractInc), { x: M + 18, y: hy, size: 26, font: bold, color: NAVY });
    page.drawText('GST inc', { x: M + 20 + bold.widthOfTextAtSize(money0(data.contractInc), 26), y: hy + 3, size: 11, font, color: GREY }); hy -= 20;
    for (const ln of wrap(`${money0(data.contractEx)} ex GST · paid across ${int0(data.parcels)} parcels a year (${money(data.parcelRateEx, 3)}/parcel + GST). Express Post, Depot & DoE are paid as separate fees on top.`, font, 10, COLW - 36)) {
      page.drawText(ln, { x: M + 18, y: hy, size: 10, font, color: rgb(0.23, 0.25, 0.29) }); hy -= 13;
    }
    y -= hH + 6;
  }

  // Fees
  h2('Your fees');
  ensure(16); text(`Total parcels delivered / yr: `, M, 10, font, INK);
  text(int0(data.parcels), M + font.widthOfTextAtSize('Total parcels delivered / yr: ', 10), 10, bold, INK); y -= 16;
  table(
    [{ label: 'FEE', w: COLW - 300 }, { label: 'PROJECTED / YR', w: 110, align: 'right' }, { label: 'RATE (EX GST)', w: 95, align: 'right' }, { label: 'RATE (+GST)', w: 95, align: 'right' }],
    data.fees.map((f) => [{ t: f.label + (f.contract ? '  — contract' : ''), bold: true }, int0(f.volYr), money(f.rateEx, 3), money(f.rateInc, 3)]),
  );
  para('The contract fee is paid across your parcels. Express Post, Depot bags & DoE are paid per item as separate fees on top of your parcel earnings.', 8.5, GREY);

  // How the day is paid — the payable-time terms, when the offer is priced that way.
  if (data.payable) {
    const p = data.payable;
    h2('How your day is paid');
    table(
      [{ label: 'PART OF YOUR DAY', w: COLW - 250 }, { label: 'MEASURED FROM', w: 150 }, { label: 'PAID', w: 100, align: 'right' }],
      [
        [{ t: 'Depot — sorting & loading', bold: true }, 'Start to scanner sign-off', { t: `${p.indoorPace.toFixed(0)} parcels/hr`, bold: true }],
        [{ t: 'On the road — delivering', bold: true }, 'Scanner sign-off to finish', { t: 'in full, actual', bold: true }],
      ],
    );
    para(`Your depot time is paid as an allowance of one hour per ${p.indoorPace.toFixed(0)} parcels — on ${p.parcelsPerDay.toFixed(0)} parcels that is ${hhmm(p.depotAllowanceH)}. Clearing the depot faster than that is time back in your day, and it is yours. Your road time is paid in full, however long the round takes, from the moment you sign off your scanner and photograph the summary in the portal. That photo is what starts your road clock, so take it as soon as you are loaded.`);
    para(`Your rate of ${money(p.rate, 3)} per parcel is built as ${money(p.depotLeg, 3)} for the depot + ${money(p.roadLeg, 3)} for the road${p.loadPct > 0 ? ` + ${money(p.loading, 3)} for the difficulty of your round` : ''} — a payable day of ${hhmm(p.payableH)} on ${p.parcelsPerDay.toFixed(0)} parcels.`, 9, GREY);
  }

  // Pace. Each table is kept whole on one page — a driver reading the under-target rows
  // on one sheet and the upside on the next reads half the offer.
  ensure(120);
  h2('Delivering faster earns you more');
  if (data.paceOnRoad) {
    para(`Your fee is per parcel, so the faster you work the more you earn per hour. Your day has two parts and you can move either one. The three tables below are the same deal read three ways — pick whichever one you think in.`);
  } else if (data.paceRange) {
    para(`Your fee is per parcel — the quicker you clear your round, the more you earn per hour. This round is expected to run at ${data.paceRange.low.toFixed(1)}-${data.paceRange.high.toFixed(1)} parcels/hr, and your rate is set on that expectation. Beat it and you earn more per hour; if it runs slower, the IR Safety Net below protects you.`);
  } else {
    para(`Your fee is per parcel — the quicker you clear your round, the more you earn per hour. You currently average ${data.pph.toFixed(1)} parcels/hr.`);
  }
  para(`${money(data.awardHr)}/hr is the target to clear — it is also the NSW Contract Determination safety net for your vehicle class (${data.vClass}), so you are never paid less than it. Any pace below is short of the target; every pace above it, the extra is yours to keep.`);

  (data.paceTables || []).forEach((t) => {
    // ensure() the WHOLE block, so it never splits — the border below is drawn as one
    // rectangle from the block's top to its bottom and could not survive a page break.
    ensure(96 + t.paceRows.length * 20);
    y -= 10;
    const boxTop = y + 8;
    text(t.title, M, 10.5, bold, NAVY); y -= 14;
    if (t.holds) { text(t.holds, M, 9, font, GREY); y -= 14; }
    if (t.breakEven > 0) {
      // Show the working. Without it a single-leg break-even looks like bad arithmetic:
      // the obvious sum is award / rate, but the OTHER leg has already taken its share
      // out of every parcel's fee, so this leg's break-even is award / (rate - that share).
      if (t.mode === 'road' || t.mode === 'depot') {
        const otherPace = t.mode === 'road' ? t.depotPace : t.roadPace;
        const otherLbl = t.mode === 'road' ? 'depot allowance' : 'road time';
        const otherShare = data.awardHr / otherPace;
        const thisShare = data.parcelRateEx - otherShare;
        para(`${money(otherShare, 3)} of your ${money(data.parcelRateEx, 3)} pays for your ${otherLbl}, leaving ${money(thisShare, 3)}, so you hit the target here at ${money(data.awardHr)} / ${money(thisShare, 3)} = ${t.breakEven.toFixed(1)} an hour.`, 9);
      } else {
        para(`Across the whole day it is simply ${money(data.awardHr)} / ${money(data.parcelRateEx, 3)} = ${t.breakEven.toFixed(1)} parcels an hour.`, 9);
      }
    }
    table(
      [{ label: t.unit.toUpperCase(), w: COLW - 280 }, { label: 'YOUR $/HR (EX GST)', w: 140, align: 'right' }, { label: 'AGAINST THE TARGET', w: 140, align: 'right' }],
      t.paceRows.map((r) => {
        const pLbl = Number.isInteger(r.p) ? String(r.p) : r.p.toFixed(1);
        if (r.isMin) {
          const row = [{ t: `${pLbl}  — the target pace${r.now ? ', and your pace now' : ''}`, bold: true, color: GOLD },
            { t: money(r.hr), bold: true, color: GOLD }, { t: 'you hit the target', bold: true, color: GOLD }];
          row._hl = true; return row;
        }
        const lbl = r.now ? ((data.paceRange && !data.paceOnRoad) ? '  (expected pace)' : '  (your pace now)') : '';
        // Rows under the floor are shown, greyed, with the shortfall named. They read as
        // "under target", not "topped up": both are true, but the guarantee is the floor
        // he stands on, not the thing he is working toward. Starting at the expected pace
        // would flatter the offer — showing the slow end is what makes the target legible.
        const gap = data.awardHr - r.hr;
        const col = r.below ? GREY : INK;
        const row = [{ t: `${pLbl}${lbl}`, color: col }, { t: money(r.hr), color: col },
          r.below ? { t: `${money(gap)} under target`, color: GREY } : { t: `+${money(-gap)}`, color: GREEN, bold: true }];
        if (r.now) row._hl = true; return row;
      }),
    );
    // Box the block. Drawn last (an unfilled outline sits outside the content, so it
    // can't cover anything) because the height is only known once the rows are laid out.
    page.drawRectangle({ x: M - 11, y: y - 2, width: COLW + 22, height: boxTop - y + 2,
      borderColor: CREAM_BD, borderWidth: 1 });
    y -= 12;
  });

  // Speed bonus — the two-tier rate. Sits before the generic bonus boxes because it is
  // part of the deal, not upside: on a per-parcel fee it is the ONLY thing that turns
  // going faster into more money.
  if (data.speedBonus) {
    const b = data.speedBonus;
    h2('Earn the higher rate — your speed bonus');
    table(
      [{ label: 'YOUR RATE PER PARCEL', w: COLW - 220 }, { label: 'EX GST', w: 110, align: 'right' }, { label: '+GST', w: 110, align: 'right' }],
      [
        [{ t: 'Standard rate' }, money(b.base, 3), money(b.base * 1.1, 3)],
        [{ t: 'On-target rate — every parcel that period', bold: true, color: GREEN }, { t: money(b.onTarget, 3), bold: true, color: GREEN }, { t: money(b.onTarget * 1.1, 3), bold: true, color: GREEN }],
      ],
    );
    const how = b.basis === 'road'
      ? `How to earn it: deliver ${b.roadTarget.toFixed(1)} parcels an hour on the road — measured from the moment you sign off your scanner to the moment you finish. On a typical ${Math.round(b.typicalParcels)}-parcel day that is a ${b.roundHours} round.`
      : `How to earn it: sign off your scanner and capture the summary in the portal by your start time of ${b.startStr} plus one hour for every ${b.indoorPace.toFixed(0)} parcels. On a typical ${Math.round(b.typicalParcels)}-parcel day that means being away by ${b.typicalTarget}.`;
    para(`${how} Do that on ${b.hitPct.toFixed(0)}% of the days in a period and EVERY parcel in that period is paid at the higher rate — not just the days you hit it.`);
    para(`That is worth about ${money0(b.perPeriod)} extra a period, or ${money0(b.perYear)} a year. Both ends are recorded for you — the scanner photo is timestamped when you take it, and your finish is logged where you finish — so none of it depends on paperwork. If Australia Post's freight is late that day, the day does not count.`, 9, GREY);
  }

  // Bonuses
  const bonusBox = (title, body) => {
    const lines = wrap(body, font, 10, COLW - 32);
    const bH = 22 + lines.length * 13 + 8; ensure(bH + 6); y -= 4;
    page.drawRectangle({ x: M, y: y - bH, width: COLW, height: bH, color: rgb(0.933, 0.969, 0.933), borderColor: rgb(0.81, 0.9, 0.81), borderWidth: 1 });
    page.drawRectangle({ x: M, y: y - bH, width: 5, height: bH, color: GREEN });
    let by = y - 16; page.drawText(san(title), { x: M + 16, y: by, size: 11, font: bold, color: NAVY }); by -= 16;
    lines.forEach((ln) => { page.drawText(ln, { x: M + 16, y: by, size: 10, font, color: rgb(0.23, 0.25, 0.29) }); by -= 13; });
    y -= bH + 6;
  };
  // Omitted entirely when the round carries no express — see buildOfferData.
  if (data.bonus1) bonusBox('Bonus potential — improve your income with Express Post',
    `Express Post is uncapped upside, paid on top of your contract. At ${int0(data.bonus1.perWeek)} EP/week (${int0(data.bonus1.perYr)}/yr) that's +${money0(data.bonus1.bonusYrInc)}/yr — every EP earns you ${money(data.bonus1.epRateInc)} inc.`);
  bonusBox('Bonus potential — assist with overflow parcels',
    `Help clear overflow parcels from other routes — each is paid at your parcel fee of ${money(data.bonus2.parcelFeeInc, 3)} inc. On a day you take an extra ${int0(data.bonus2.perDay)}, that's +${money(data.bonus2.dayBonusInc)} that day — on top, whenever it's needed.`);

  // Current vs proposed (kept together)
  // Current vs proposed is deliberately absent from the driver-facing document — it is a
  // manager's sanity check only (Driver Review section 5 still shows it).
  // Footer award line on the last page
  ensure(30); y -= 6;
  page.drawLine({ start: { x: M, y }, end: { x: RIGHT, y }, thickness: 0.5, color: LINE }); y -= 6;
  // A negative "sits -7.4% above" is both self-contradictory and the worst sentence that
  // could appear here — it advertises that the offer is under the floor. Below the floor,
  // state the guarantee instead; the pace tables already show how he clears it.
  para(data.award.pct >= 0
    ? `This offer sits ${data.award.pct.toFixed(1)}% above the NSW Transport Industry IR Safety Net award minimum of ${money(data.award.awardHr)}/hr for your vehicle class (${data.vClass}), effective ${data.ratesEffective} — your parcel contract works out to ${money(data.award.hourly)}/hr at ${data.paceRange ? 'the expected pace' : 'your current pace'}.`
    : `The NSW Transport Industry IR Safety Net guarantees you a minimum of ${money(data.award.awardHr)}/hr for your vehicle class (${data.vClass}), effective ${data.ratesEffective}, and you are never paid less than it. Working at the paces above is what carries you past it.`, 8.5, GREY);
  if (data.paceRange) {
    para(`IR Safety Net protection: your per-parcel rate is set on an expected pace of ${data.paceRange.low.toFixed(1)}-${data.paceRange.high.toFixed(1)} parcels/hr. This is our estimate of the round, not a figure you carry the risk on. If the round runs slower than expected, you are still guaranteed the ${money(data.award.awardHr)}/hr minimum safety-net rate for your vehicle class — the contracted per-parcel rate never leaves you below that floor. You keep any upside above it.`, 8.5, GREY);
  }
  para('Projected parcel, Express Post, Depot and DoE volumes are indicative only, based on recorded delivery history. Actual volumes are driven by Australia Post demand and are NOT guaranteed. All fees are paid per item, so your earnings move up and down with volume. Indicative offer summary for discussion; the binding terms are in the Subcontractor Agreement you will sign.', 8.5, GREY);

  // Page numbers
  const pages = pdf.getPages();
  pages.forEach((pg, i) => {
    const s = `Page ${i + 1} of ${pages.length}`;
    pg.drawText(s, { x: RIGHT - font.widthOfTextAtSize(s, 7), y: 26, size: 7, font, color: GREY });
    pg.drawText(san(`Contract Offer · ${data.name} · ${data.today}`), { x: M, y: 26, size: 7, font, color: GREY });
  });

  return pdf.save();
}

// Uint8Array → base64 (chunked), for the upload payload.
export function offerBytesToBase64(bytes) {
  let binary = ''; const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(binary);
}
